/* eslint-disable array-bracket-newline */
/**
 * Implements a limit proxy modal
 * Allows to withdrawal coins
 */
import { Web3Provider } from '@ethersproject/providers';
import { Network } from '@kattana-io/common';
import { LimitOrdersProxy } from '@kattana/limit-orders';
import { ethers } from 'ethers';
import { useEffect, useState } from 'react';
import { useAccount, useNetwork } from 'wagmi';
import Text from '../../../atoms/Text';
import useDevice from '../../../pages/MainPage/useMobileDetect';
import { KattanaAPI } from '../../../store/createStore';
import Loader from '../../common/Loader';
import TokensTableMobile from './mobile/TokensTable';
import StatsTable from './StatsTable';
import TokensTable from './TokensTable';

interface ILimitProxyModalProps {
  onCancel: () => void;
}

interface ILPMState {
  loading: boolean;
  proxy: string;
  stage: Stages;
  tokens?: any[];
}

enum Stages {
  INIT,
  CHECKING,
  NO_DATA,
  NO_WALLET,
  NO_PROXY,
  LOADING_COINS,
  DONE,
}

const NO_ADDR = '';

const initialState = {
  loading: true,
  proxy: NO_ADDR,
  stage: Stages.INIT,
  tokens: [],
};

const ProxyAbi =
  '[{"inputs":[{"internalType":"bytes32","name":"_key","type":"bytes32"}],"name":"params","outputs":[{"internalType":"bytes32","name":"","type":"bytes32"}],"stateMutability":"view","type":"function"},{"inputs":[],"name":"owner","outputs":[{"internalType":"address payable","name":"","type":"address"}],"stateMutability":"view","type":"function"},{"inputs":[{"internalType":"address payable","name":"_recepient","type":"address"},{"internalType":"uint256","name":"_amount","type":"uint256"}],"name":"demandETH","outputs":[],"stateMutability":"nonpayable","type":"function"},{"inputs":[{"internalType":"contract IERC20","name":"_token","type":"address"},{"internalType":"address","name":"_recepient","type":"address"},{"internalType":"uint256","name":"_amount","type":"uint256"}],"name":"demandERC20","outputs":[],"stateMutability":"nonpayable","type":"function"},{"inputs":[{"internalType":"contract IERC20[]","name":"_tokens","type":"address[]"},{"internalType":"address payable","name":"_recepient","type":"address"}],"name":"demandAll","outputs":[],"stateMutability":"nonpayable","type":"function"},{"inputs":[{"internalType":"address payable","name":"_target","type":"address"},{"internalType":"uint256","name":"_value","type":"uint256"},{"internalType":"bytes","name":"_data","type":"bytes"}],"name":"demand","outputs":[{"internalType":"bool","name":"","type":"bool"},{"internalType":"bytes","name":"","type":"bytes"}],"stateMutability":"nonpayable","type":"function"}]';

const isNative = (token: string): boolean =>
  token.toLocaleLowerCase() == '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee';

/**
 * Return address of proxy wallet or NO_ADDR
 * @param lp - proxy class
 * @param address - string of wallet
 * @returns
 */
const getProxyAddress = async (lp: LimitOrdersProxy, address: string) => {
  try {
    console.log('limit modal: checking if wallet has proxy');
    const value = await lp.walletHasProxy(address);
    if (value) {
      const proxy = await lp.getWalletProxyAddress(address);
      return proxy ? proxy : NO_ADDR;
    } else {
      return NO_ADDR;
    }
  } catch (e) {
    console.warn(e);
    return NO_ADDR;
  }
};

/**
 * Return list of proxy tokens
 * @param id - id of chain
 * @param address - proxy
 */
const getProxyTokens = async (id: number, address: string) => {
  try {
    const tokens = await KattanaAPI.balances(id, address);
    return {
      tokens,
      ok: true,
    };
  } catch (e) {
    console.warn(e);
    return {
      tokens: [],
      ok: false,
    };
  }
};

const LimitProxyModal = ({ onCancel }: ILimitProxyModalProps) => {
  const [state, setState] = useState<ILPMState>(initialState);
  const { isMobile } = useDevice();
  const { address } = useAccount();
  const { chain } = useNetwork();

  /**
   * Stage 1: Check if user has any wallet
   */
  useEffect(() => {
    if (address && chain) {
      setState({
        loading: true,
        proxy: NO_ADDR,
        stage: Stages.CHECKING,
      });
      const chId = chain?.id;
      const lp = new LimitOrdersProxy(chId);
      getProxyAddress(lp, address).then((proxy: string) => {
        setState((state) => ({
          ...state,
          proxy,
          stage: Stages.LOADING_COINS,
        }));
      });
    } else {
      setState({
        loading: false,
        stage: Stages.NO_WALLET,
        proxy: NO_ADDR,
      });
    }
  }, [chain, address]);

  /**
   * Stage 2: Fetch coins
   */
  useEffect(() => {
    if (state.stage == Stages.LOADING_COINS) {
      if (state.proxy == NO_ADDR) {
        setState((state) => ({
          ...state,
          loading: false,
          stage: Stages.NO_PROXY,
        }));
      } else {
        const chId = Number(chain?.id);
        getProxyTokens(chId, state.proxy).then((resp) => {
          const { ok, tokens } = resp;
          if (ok) {
            setState((state) => ({
              ...state,
              loading: false,
              stage: Stages.DONE,
              tokens: tokens as any[],
            }));
          } else {
            setState((state) => ({
              ...state,
              loading: false,
              stage: Stages.NO_DATA,
              tokens: [],
            }));
          }
        });
      }
    }
  }, [state.proxy, state.stage, chain?.id]);

  const onScan = () => {
    const network = Network.fromChainId(chain?.id);
    const link = network?.getScanForWallet() || '';
    window.open(`${link}${state.proxy}`);
  };

  const onWithdraw = async (
    token: string,
    decimals: number,
    balance: string
  ) => {
    console.log(
      'limits modal: trying to demand ',
      token,
      ' with amount: ',
      balance
    );

    if (window) {
      const injectedEth = (window as any).ethereum;
      const provider = injectedEth
        ? new Web3Provider((window as any).ethereum)
        : null;

      if (provider) {
        const signer = provider.getSigner(address);
        const proxyContract = new ethers.Contract(
          state.proxy,
          ProxyAbi,
          signer
        );

        if (isNative(token)) {
          try {
            const tx = await proxyContract.demandETH(
              address,
              ethers.BigNumber.from(balance)
            );
            const transactionReceipt = await tx.wait();
            console.log('limits modal: demandETH ', transactionReceipt);
          } catch (e) {
            console.warn(e);
          }
          setState((state) => ({
            ...state,
            loading: true,
            stage: Stages.LOADING_COINS,
          }));
        } else {
          try {
            const tx = await proxyContract.demandERC20(
              token,
              address,
              ethers.BigNumber.from(balance)
            );
            const transactionReceipt = await tx.wait();
            console.log('limits modal: demandERC20 ', transactionReceipt);
          } catch (e) {
            console.warn(e);
          }
          setState((state) => ({
            ...state,
            loading: true,
            stage: Stages.LOADING_COINS,
          }));
        }
      }
    }
  };

  return (
    <>
      <div className='wallet_modal'>
        <div className='wallet_modal__header'>
          <span>
            <div className='wallet_modal__wallet__title'>{state.proxy}</div>{' '}
            on&nbsp;
            <span
              // onClick={onPairNameClick}
              className='wallet_modal__wallet__pair_name'
            >
              {chain?.name}
            </span>
          </span>
          <span
            className='wallet_modal__pill wallet_modal__scan'
            onClick={onScan}
          >
            Scan
          </span>
        </div>
        <div className='wallet_modal__close' onClick={onCancel} />
        <div className='wallet_modal__container'>
          {state.loading ? (
            <div className='wallet_modal__container__loader'>
              <Loader />
            </div>
          ) : null}
          {state.stage == Stages.NO_WALLET ? (
            <Text size={12} className='text--grey-600 text--weight-500'>
              Please connect wallet to interact with proxy contract
            </Text>
          ) : null}
          {state.stage == Stages.NO_DATA ? (
            <Text size={12} className='text--grey-600 text--weight-500'>
              Could not load tokens data for proxy contract
            </Text>
          ) : null}
          {state.stage == Stages.NO_PROXY ? (
            <Text size={12} className='text--grey-600 text--weight-500'>
              Please deploy a proxy contract to see statistics
            </Text>
          ) : null}
          {state.stage == Stages.DONE ? (
            <>
              <StatsTable tokens={state.tokens} />
              {isMobile ? (
                <TokensTableMobile
                  network={Network.fromChainId(chain?.id)}
                  tokens={state.tokens}
                  onWithdraw={onWithdraw}
                />
              ) : (
                <div className='trades__table table_desktop__container'>
                  <TokensTable
                    network={Network.fromChainId(chain?.id)}
                    tokens={state.tokens}
                    onWithdraw={onWithdraw}
                  />
                </div>
              )}
            </>
          ) : null}
        </div>
      </div>

      <div className='wallet_modal__backdrop' onClick={onCancel} />
    </>
  );
};

export default LimitProxyModal;
